import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";
import { API, ASSETS } from "@/lib/site";

const CAREERS_IMG = ASSETS.fleet;

const PERKS = [
  "Consistent miles & steady lanes",
  "Paid on time, every time",
  "Late-model, well-kept equipment",
  "Direct line to the owner",
];

const CDL = ["Class A", "Class B", "Not yet CDL"];
const EXP = ["Less than 1 year", "1–3 years", "3–5 years", "5+ years"];

const empty = { name: "", email: "", phone: "", cdl_class: "Class A", experience: "1–3 years", message: "" };

export default function Careers() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email and phone.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/driver-apply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      setDone(true);
      setForm(empty);
      toast.success("Application sent — we'll be in touch soon.");
    } catch {
      toast.error("Something went wrong. Please call us instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="careers"
      data-testid="careers"
      className="grid grid-cols-1 border-y border-white/10 lg:grid-cols-2"
    >
      <div className="relative min-h-[360px] overflow-hidden border-b border-white/10 lg:border-b-0 lg:border-r">
        <img src={CAREERS_IMG} alt="Truck driver on the road" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-ink/20" />
        <div className="absolute inset-0 flex flex-col justify-between p-8 md:p-12">
          <span className="text-xs uppercase tracking-[0.3em] text-brand">Now Hiring Drivers</span>
          <div>
            <h2 className="font-display text-4xl font-black uppercase leading-none tracking-tighter md:text-6xl">
              Drive with
              <br />
              Trey's Trucking
            </h2>
            <ul className="mt-8 space-y-3">
              {PERKS.map((p) => (
                <li key={p} className="flex items-center gap-3 text-sm text-[#d4d4d8]">
                  <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-brand" />
                  {p}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="bg-surface p-8 md:p-14">
        {done ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            data-testid="careers-success"
            className="flex h-full flex-col items-start justify-center"
          >
            <CheckCircle2 className="h-12 w-12 text-brand" />
            <h3 className="mt-6 font-display text-3xl font-bold uppercase tracking-tight">Application received</h3>
            <p className="mt-3 max-w-sm text-[#a1a1a8]">
              Thanks for your interest in driving with us. Nathan will review your application and reach out.
            </p>
            <button
              onClick={() => setDone(false)}
              className="mt-8 border border-white/25 px-6 py-3 text-sm uppercase tracking-wide transition-colors hover:border-brand hover:text-brand"
            >
              Submit another
            </button>
          </motion.div>
        ) : (
          <form onSubmit={submit} data-testid="careers-form" className="flex flex-col gap-5">
            <h3 className="font-display text-2xl font-bold uppercase tracking-tight">Apply to drive</h3>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <Field label="Full Name" testid="driver-name">
                <input value={form.name} onChange={set("name")} data-testid="driver-name" className={inputCls} placeholder="Your name" />
              </Field>
              <Field label="Phone" testid="driver-phone">
                <input value={form.phone} onChange={set("phone")} data-testid="driver-phone" className={inputCls} placeholder="(409) 000-0000" />
              </Field>
            </div>
            <Field label="Email" testid="driver-email">
              <input type="email" value={form.email} onChange={set("email")} data-testid="driver-email" className={inputCls} placeholder="you@email.com" />
            </Field>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <Field label="CDL Class">
                <select value={form.cdl_class} onChange={set("cdl_class")} data-testid="driver-cdl" className={inputCls}>
                  {CDL.map((c) => <option key={c} className="bg-surface">{c}</option>)}
                </select>
              </Field>
              <Field label="Experience">
                <select value={form.experience} onChange={set("experience")} data-testid="driver-experience" className={inputCls}>
                  {EXP.map((c) => <option key={c} className="bg-surface">{c}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Anything else?">
              <textarea value={form.message} onChange={set("message")} data-testid="driver-message" rows={3} className={inputCls} placeholder="Endorsements, availability, etc." />
            </Field>
            <button
              type="submit"
              disabled={loading}
              data-testid="driver-apply-submit"
              className="group mt-2 flex items-center justify-center gap-2 bg-brand px-8 py-4 text-sm font-semibold uppercase tracking-wide text-ink transition-colors hover:bg-white disabled:opacity-60"
            >
              {loading ? "Sending..." : "Submit Application"}
              {!loading && <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}

const inputCls =
  "w-full rounded-none border-0 border-b border-white/20 bg-transparent px-0 py-3 text-white outline-none transition-colors placeholder:text-[#71717a] focus:border-brand";

function Field({ label, children }) {
  return (
    <label className="flex flex-col gap-2">
      <span className="text-xs uppercase tracking-widest text-[#a1a1a8]">{label}</span>
      {children}
    </label>
  );
}
